import { SESSION_STATUS } from "./sessionStatus";

/**
 * mutationGate.js — shared default for the session-data mutation gate.
 *
 * Every session-data-mutating playground surface (thread posts, chat sends, graph mutations, envelope
 * edits, New Object) must be gated on BOTH an editable revision (canEditRevision) AND an open session
 * (isSessionOpen) — see PlaygroundShell.canMutateSession in playground/index.js, which publishes the
 * combined predicate on shellInterface.
 *
 * Before this extraction, callers disagreed on how to fall back when a shellInterface (a partial test
 * double, or an older/partial embedding host) doesn't expose the full seam:
 *   - the four widgets (thread, chat, graph, envelopeControls) hand-rolled
 *     `si.canEditRevision() && (!si.isSessionOpen || si.isSessionOpen())` — fail-OPEN when isSessionOpen
 *     is missing.
 *   - details/playground.js called `!!si.canMutateSession?.()` — fail-CLOSED when canMutateSession
 *     itself is missing.
 * Two different defaults for one seam is a trap for the next caller. This helper is now the single
 * source of truth: prefer the combined predicate when the shellInterface exposes it (the real
 * PlaygroundShell always does, and so does every fixture that cares about the distinction), and
 * otherwise fail-open on isSessionOpen only — the convention the widgets already established (and the
 * one their existing test fixtures assume, so unifying here avoids rewriting them).
 *
 * Pure: no state, no React.
 */
export function canMutateSession(shellInterface) {
  const si = shellInterface;
  if ( !si ) return false;
  if ( si.canMutateSession ) return !!si.canMutateSession();
  if ( !si.canEditRevision || !si.canEditRevision() ) return false;
  return !si.isSessionOpen || si.isSessionOpen();
}

// Reason codes for why a mutation is blocked, most-specific first. The order mirrors the priority
// getMutationStatus evaluates below: a role problem is always the actionable reason to surface even
// if the revision also happens to be uneditable, and a revision-phase problem always outranks a
// merely-closed session, since fixing the more specific issue is what would unblock the next tier.
export const MUTATION_REASON = {
  NO_WRITE_ACCESS: "no-write-access",
  REVISION_NOT_EDITABLE: "revision-not-editable",
  SESSION_NOT_OPEN: "session-not-open",
};

// Human-readable copy for each reason code above, colocated with the codes so every renderer (the
// details pane, T6) shows consistent wording and tests can assert on it without duplicating strings.
//
// These are ROW FRAGMENTS, not sentences: the details pane renders them as "Blocked — {fragment}" in
// the Mutations property row, so they are lowercase, unpunctuated, and lead with the subject that is
// blocking. Do NOT drop one into a tooltip or an alert as-is — it will read as a sentence stub. For
// the session-closed case a control needs to explain on its own, use SESSION_CLOSED_HINT
// (sessionStatus.js), which is the same state phrased to stand alone.
export const MUTATION_REASON_COPY = {
  [MUTATION_REASON.NO_WRITE_ACCESS]: "no write access",
  [MUTATION_REASON.REVISION_NOT_EDITABLE]: "revision is committed",
  [MUTATION_REASON.SESSION_NOT_OPEN]: "session is closed",
};

// Revision phases that accept mutations — mirrors Api.canUpdateRevision (context/api.js). Kept as a
// literal set here rather than importing api.js: this module stays free of the Api class's HTTP/auth
// surface, and api.js itself hardcodes these same two phase strings inline (no shared constant exists
// to reuse).
const EDITABLE_REVISION_PHASES = new Set(["draft", "managed"]);

/**
 * Reason-carrying counterpart to canMutateSession: instead of collapsing the gate into a single
 * boolean, reports which tier is blocking (or that none is), most-specific-first:
 *   1. the viewer lacks blob write access (public/non-member) — "no-write-access"
 *   2. the revision's phase isn't draft/managed (already committed) — "revision-not-editable"
 *   3. a session is selected but isn't open (closing/closed/deleting) — "session-not-open"
 *   4. otherwise mutations are allowed.
 *
 * `session` is the current session object, or null/undefined when none is selected. No-session is
 * NOT itself a blocking reason: with no session in play there's nothing session-specific to reopen,
 * so the result depends only on blob/revision.
 *
 * Pure: no state, no React. Takes explicit dependencies (not a shellInterface duck type) because it
 * needs the raw blob/revision/session rather than the collapsed booleans shellInterface exposes.
 */
export function getMutationStatus({ api, blob, revision, session }) {
  if ( !api.haveBlobWriteAccess(blob) ) {
    return { allowed: false, reason: MUTATION_REASON.NO_WRITE_ACCESS };
  }

  if ( !EDITABLE_REVISION_PHASES.has(revision.phase) ) {
    return { allowed: false, reason: MUTATION_REASON.REVISION_NOT_EDITABLE };
  }

  if ( session && session.status !== SESSION_STATUS.OPEN ) {
    return { allowed: false, reason: MUTATION_REASON.SESSION_NOT_OPEN };
  }

  return { allowed: true, reason: null };
}
